import React, { useMemo } from 'react';
import {
    SegmentLoader,
    GroupeAccordeons,
    AccordeonMi,
    Row,
    Col,
} from '@design-system-etat/dsfr-react';
import * as PropTypes from 'prop-types';
import { getDateFRFromYYYYMMDDHHMMSS } from 'components/Commun/Form/Date/dateTranformation';
import BoutonFichierTelechargeable from 'components/Commun/Fichier/BoutonFichierTelechargeable';
import styles from './DerouleArmeComponent.module.scss';
import DerouleArmeElementArmeComponent from './components/DerouleArmeElementArmeComponent';
import DerouleArmeCanonComponent from './components/DerouleArmeCanonComponent';
import DerouleArmePersonneComponent from './components/DerouleArmePersonneComponent';

const DerouleArmeComponent = ({ data }) => {
    const idInformations = data && data.uuid ? `informations-${data.uuid}` : 'informations';
    const idElementsArme = data && data.uuid ? `elementsArme-${data.uuid}` : 'elementsArme';
    const idLocalisation = data && data.uuid ? `localisation-${data.uuid}` : 'localisation';
    const dateDetention =
        data && data.dateDetention ? getDateFRFromYYYYMMDDHHMMSS(data.dateDetention) : null;

    const elementsArmeTriees = useMemo(() => {
        if (!data) {
            return [];
        }

        return data.elementsArme.sort(
            (element1, element2) => element1.ordreType - element2.ordreType,
        );
    }, [data]);

    const canonsTriees = useMemo(() => {
        if (!data) {
            return [];
        }

        return data.canons.sort(
            (element1, element2) => element1.numeroCanon - element2.numeroCanon,
        );
    }, [data]);

    const personnes = useMemo(() => {
        if (!data) {
            return [];
        }

        return data.personnes;
    }, [data]);

    const genererFicheSituation = () => {
        if (data && data.ficheSituation.action && data.uuid) {
            data.ficheSituation.action(data.uuid);
        }
    };

    const affichageFicheSituation = () => {
        if (data && data.ficheSituation) {
            return (
                <div className={styles['DerouleArme-ficheSituation']}>
                    <BoutonFichierTelechargeable
                        handleOnClick={genererFicheSituation}
                        label="Fiche de situation"
                        isLoading={data.ficheSituation.isLoading}
                        titreFichier={data.ficheSituation.titreFichier}
                        mimeType={data.ficheSituation.mimeType}
                        contenu={data.ficheSituation.contenu}
                    />
                </div>
            );
        }
        return <></>;
    };

    return (
        <SegmentLoader>
            <GroupeAccordeons accordeonActif={idInformations}>
                <AccordeonMi titre="Information sur l'arme" id={idInformations}>
                    <Row>
                        {dateDetention && (
                            <Col col={6} xl={3}>
                                Détenue depuis le :
                                <br />
                                <div className={styles['DerouleArme-labelElement']}>
                                    {dateDetention}
                                </div>
                            </Col>
                        )}

                        <Col col={6} xl={3}>
                            Référence RGA :
                            <br />
                            <div className={styles['DerouleArme-labelElement']}>
                                {data ? data.rga.numeroRGA : null}
                            </div>
                        </Col>
                        <Col col={6} xl={3}>
                            Classement européen :
                            <br />
                            <div className={styles['DerouleArme-labelElement']}>
                                {data ? data.rga.classementEuropeen : null}
                            </div>
                        </Col>
                        <Col col={6} xl={3}>
                            Mode de fonctionnement :
                            <br />
                            <div className={styles['DerouleArme-labelElement']}>
                                {data ? data.rga.modeFonctionnement : null}
                            </div>
                        </Col>
                        <Col col={6} xl={3}>
                            Système d&apos;alimentation :
                            <br />
                            <div className={styles['DerouleArme-labelElement']}>
                                {data ? data.rga.modeAlimentation : null}
                            </div>
                        </Col>
                        <Col col={6} xl={3}>
                            Longueur de l&apos;arme :
                            <br />
                            <div className={styles['DerouleArme-labelElement']}>
                                {data ? data.rga.longueurArme : null}
                            </div>
                        </Col>
                        <Col col={6} xl={3}>
                            Capacité totale :
                            <br />
                            <div className={styles['DerouleArme-labelElement']}>
                                {data
                                    ? data.rga.capaciteEnChambre + data.rga.capaciteHorsChambre
                                    : null}
                                {' = '}
                                {data ? data.rga.capaciteEnChambre : null} +{' '}
                                {data ? data.rga.capaciteHorsChambre : null}
                            </div>
                        </Col>
                    </Row>
                </AccordeonMi>
                <AccordeonMi
                    titre="Information sur les éléments essentiels de l'arme"
                    id={idElementsArme}
                >
                    <Row>
                        {elementsArmeTriees.map((elementArme) => (
                            <Col lg={3} className={styles['DerouleArme-card']}>
                                <DerouleArmeElementArmeComponent elementArme={elementArme} />
                            </Col>
                        ))}
                    </Row>
                    <Row>
                        {canonsTriees.map((canon) => (
                            <Col lg={3} className={styles['DerouleArme-card']}>
                                <DerouleArmeCanonComponent canon={canon} />
                            </Col>
                        ))}
                    </Row>
                </AccordeonMi>
                <AccordeonMi titre="Localisation de l'arme" id={idLocalisation}>
                    <Row>
                        {personnes.map((personne) => (
                            <Col className={styles['DerouleArme-card']}>
                                <DerouleArmePersonneComponent personne={personne} />
                            </Col>
                        ))}
                    </Row>
                </AccordeonMi>
            </GroupeAccordeons>
            {affichageFicheSituation()}
        </SegmentLoader>
    );
};

DerouleArmeComponent.defaultProps = {
    data: null,
};

DerouleArmeComponent.propTypes = {
    data: PropTypes.shape({
        uuid: PropTypes.string,
        dateDetention: PropTypes.string,
        elementsArme: PropTypes.arrayOf(
            PropTypes.shape({
                libelleType: PropTypes.string.isRequired,
                numeroSerie: PropTypes.string.isRequired,
                numeroEncodage: PropTypes.string.isRequired,
            }),
        ).isRequired,
        canons: PropTypes.arrayOf(
            PropTypes.shape({
                numeroCanon: PropTypes.number.isRequired,
                numeroSerie: PropTypes.string,
                numeroEncodage: PropTypes.string,
                rgaCanon: PropTypes.shape({
                    libelleCalibre: PropTypes.string.isRequired,
                    longueur: PropTypes.string.isRequired,
                    percussion: PropTypes.string.isRequired,
                    typeCanon: PropTypes.string.isRequired,
                }),
            }),
        ).isRequired,
        personnes: PropTypes.arrayOf(
            PropTypes.shape({
                numeroSia: PropTypes.string,
                categorieLibelle: PropTypes.string.isRequired,
                libelleRelation: PropTypes.string.isRequired,
                libellePersonne: PropTypes.string.isRequired,
                adresse: PropTypes.shape({
                    numeroVoie: PropTypes.string,
                    extension: PropTypes.string,
                    libelleVoie: PropTypes.string,
                    lieuDit: PropTypes.string,
                    codePostal: PropTypes.string,
                    ville: PropTypes.string,
                    pays: PropTypes.shape({
                        libelle: PropTypes.string.isRequired,
                    }).isRequired,
                }),
            }),
        ).isRequired,
        rga: PropTypes.shape({
            numeroRGA: PropTypes.string.isRequired,
            classementEuropeen: PropTypes.string.isRequired,
            modeFonctionnement: PropTypes.string.isRequired,
            modeAlimentation: PropTypes.string.isRequired,
            longueurArme: PropTypes.number.isRequired,
            capaciteEnChambre: PropTypes.number.isRequired,
            capaciteHorsChambre: PropTypes.number.isRequired,
        }),
        ficheSituation: PropTypes.shape({
            action: PropTypes.func,
            titreFichier: PropTypes.string,
            mimeType: PropTypes.string,
            contenu: PropTypes.string,
            isLoading: PropTypes.bool,
        }),
    }),
};

export default DerouleArmeComponent;
